<template>
  <div class="flex flex-col gap-4">
    <!-- Back Button -->
    <div class="flex items-center mb-2">
      <button
        type="button"
        class="flex items-center gap-2 px-3 py-2 bg-transparent border-none text-vault-blue-400 font-semibold text-sm cursor-pointer rounded-lg transition-all duration-200 hover:bg-vault-blue-400/8"
        @click="handleBack"
      >
        <ArrowLeft :size="20" />
        {{ $t('common.back') }}
      </button>
    </div>

    <!-- Header -->
    <h2 class="m-0 mb-1.5 text-[22px] text-vault-text-primary font-bold">
      {{ $t('depositWidget.moonpay.title', { currency }) }}
    </h2>
    <p class="m-0 mb-4 text-vault-text-secondary text-sm">
      {{ $t('depositWidget.moonpay.subtitle') }}
    </p>

    <!-- Loading State -->
    <div
      v-if="widgetLoading"
      class="relative w-full min-h-[400px] bg-white/50 rounded-xl overflow-hidden shadow-sm flex items-center justify-center px-5 py-10 sm:min-h-[350px] sm:px-4 sm:py-7.5"
    >
      <div class="flex flex-col items-center justify-center gap-4">
        <div class="spinner"></div>
        <p class="text-vault-text-secondary text-sm">
          {{ $t('depositWidget.moonpay.loading') }}
        </p>
      </div>
    </div>

    <!-- Error State -->
    <div
      v-if="widgetError"
      class="relative w-full min-h-[400px] bg-white/50 rounded-xl overflow-hidden shadow-sm flex items-center justify-center px-5 py-10 sm:min-h-[350px] sm:px-4 sm:py-7.5"
    >
      <div class="flex flex-col items-center justify-center gap-4 text-vault-danger-400 text-center p-5">
        <AlertCircle :size="32" />
        <p class="text-sm max-w-[300px]">{{ widgetError }}</p>
        <button
          type="button"
          class="btn-success !px-5 !py-2.5 !text-sm"
          @click="initWidget"
        >
          Retry
        </button>
      </div>
    </div>

    <!-- Success State - Widget will open automatically -->
    <div
      v-if="!widgetLoading && !widgetError && widgetOpen"
      class="relative w-full min-h-[400px] bg-white/50 rounded-xl overflow-hidden shadow-sm flex items-center justify-center px-5 py-10 sm:min-h-[350px] sm:px-4 sm:py-7.5"
    >
      <div class="flex flex-col items-center gap-4 text-center">
        <CreditCard :size="48" class="text-green-600/80 mb-2" />
        <h3 class="m-0 text-xl font-semibold text-vault-text-primary sm:text-lg">
          Payment window opened
        </h3>
        <p class="m-0 text-sm text-vault-text-secondary max-w-[320px]">
          Complete your purchase in the MoonPay overlay window
        </p>
        <p class="m-0 text-xs text-vault-text-secondary/70">
          (You can close this overlay using the X button)
        </p>
      </div>
    </div>

    <!-- Info Alert -->
    <div class="flex items-center px-4 py-3 bg-amber-500/8 border border-amber-500/30 rounded-lg">
      <div class="flex items-center gap-2">
        <span class="text-[13px] text-vault-text-primary leading-relaxed">
          Using MoonPay sandbox mode. Real payments disabled.
        </span>
      </div>
    </div>

    <!-- Fullscreen Backdrop with Close Button (shown when overlay is open) -->
    <Transition name="fade">
      <div
        v-if="widgetOpen && !showCloseConfirm"
        class="moonpay-backdrop"
        @click.self="handleOverlayBackdropClick"
      >
        <button
          type="button"
          class="moonpay-close-button"
          @click="handleCloseAttempt"
          aria-label="Close MoonPay"
        >
          <X :size="24" />
        </button>
      </div>
    </Transition>

    <!-- Close Confirmation Modal -->
    <Teleport to="body">
      <Transition name="modal-fade">
        <div
          v-if="showCloseConfirm"
          class="moonpay-confirm-modal-backdrop"
          @click.self="showCloseConfirm = false"
        >
          <div class="moonpay-confirm-modal">
            <div class="flex flex-col items-center gap-4">
              <AlertCircle :size="48" class="text-amber-500" />
              <h3 class="m-0 text-lg font-semibold text-vault-text-primary">
                Close payment window?
              </h3>
              <p class="m-0 text-sm text-vault-text-secondary text-center max-w-[300px]">
                Are you sure you want to close the MoonPay payment window? Your transaction may not be completed.
              </p>
              <div class="flex gap-3 w-full">
                <button
                  type="button"
                  class="flex-1 px-4 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-lg transition-colors"
                  @click="showCloseConfirm = false"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  class="flex-1 px-4 py-2.5 bg-vault-danger-400 hover:bg-vault-danger-500 text-white font-semibold rounded-lg transition-colors"
                  @click="confirmClose"
                >
                  Close Anyway
                </button>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { ArrowLeft, AlertCircle, CreditCard, X } from 'lucide-vue-next'

const { t: $t } = useI18n()

const props = defineProps({
  address: {
    type: String,
    required: true
  },
  currency: {
    type: String,
    default: 'USDC'
  },
  currencyInfo: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['back', 'transaction-started', 'transaction-completed'])

const widgetLoading = ref(true)
const widgetError = ref(null)
const widgetOpen = ref(false)
const showCloseConfirm = ref(false)
let moonPaySdk = null

// Get network from currencyInfo or fallback to default
const networkId = computed(() => {
  if (props.currencyInfo?.networks && props.currencyInfo.networks.length > 0) {
    return props.currencyInfo.networks[0].networkId
  }
  return 'base_sepolia' // Default fallback
})

// Map currency to MoonPay code based on network
const getCurrencyCode = (currency, network) => {
  // Handle different networks
  const isMainnet = !network.includes('sepolia') && !network.includes('testnet')

  // Currency mapping with network awareness
  const mapping = {
    'USDC': isMainnet ? 'usdc_base' : 'usdc_base', // USDC on Base (both mainnet and testnet)
    'USDT': 'usdt', // USDT (typically Ethereum)
    'ETH': network.includes('base') ? 'eth_base' : (isMainnet ? 'eth' : 'eth_sepolia'),
    'BASE': 'eth_base', // Base network native token
    'BTC': 'btc',
    'cbBTC': 'btc', // Fallback to BTC (cbBTC may not be supported yet)
    'BNB': 'bnb', // BNB on BSC
  }

  return mapping[currency] || (isMainnet ? 'usdc_base' : 'eth_sepolia')
}

const initWidget = async () => {
  widgetLoading.value = true
  widgetError.value = null

  try {
    // Import and load MoonPay SDK (loads external script)
    const { loadMoonPay } = await import('@moonpay/moonpay-js')
    const moonPayInit = await loadMoonPay()

    const apiKey = import.meta.env.VITE_MOONPAY_API_KEY || 'pk_test_rIAxj3p5kR8X4nuOSNgxbQDUkwx9U78'
    const environment = import.meta.env.VITE_MOONPAY_ENVIRONMENT || 'sandbox'
    const currencyCode = getCurrencyCode(props.currency, networkId.value)

    console.log('🌙 Initializing MoonPay SDK:', {
      currency: props.currency,
      network: networkId.value,
      currencyCode,
      address: props.address,
      apiKey: apiKey.substring(0, 15) + '...' // Log partial key for debugging
    })

    // Initialize MoonPay SDK
    moonPaySdk = moonPayInit({
      flow: 'buy',
      environment: environment,
      variant: 'overlay', // Use overlay variant (will be styled fullscreen with CSS)
      params: {
        apiKey: apiKey,
        currencyCode: currencyCode,
        walletAddress: props.address,
        baseCurrencyCode: 'usd',
        baseCurrencyAmount: '50', // Default $50
        showWalletAddressForm: false,
        lockAmount: false
      },
      handlers: {
        async onTransactionCompleted(transactionData) {
          console.log('✅ MoonPay transaction completed:', transactionData)
          widgetOpen.value = false
          emit('transaction-completed', transactionData)
        },
        onCloseOverlay() {
          console.log('🚪 MoonPay overlay closed by user')
          widgetOpen.value = false
          showCloseConfirm.value = false
        }
      }
    })

    widgetLoading.value = false

    // Auto-open widget immediately
    openWidget()
  } catch (error) {
    console.error('❌ Failed to initialize MoonPay:', error)
    widgetError.value = 'Failed to load MoonPay SDK. Please check your API key or try again later.'
    widgetLoading.value = false
  }
}

const openWidget = () => {
  if (!moonPaySdk) {
    widgetError.value = 'Widget not initialized'
    return
  }

  try {
    widgetOpen.value = true
    emit('transaction-started')
    moonPaySdk.show()
    console.log('🚀 MoonPay widget opened')
  } catch (error) {
    console.error('❌ Failed to open MoonPay widget:', error)
    widgetError.value = 'Failed to open payment window'
    widgetOpen.value = false
  }
}

const handleCloseAttempt = () => {
  showCloseConfirm.value = true
}

const handleOverlayBackdropClick = () => {
  // Optional: handle backdrop click (currently do nothing, require explicit X button click)
  // handleCloseAttempt()
}

const confirmClose = () => {
  showCloseConfirm.value = false
  widgetOpen.value = false

  // Close MoonPay SDK overlay
  if (moonPaySdk) {
    try {
      // MoonPay SDK doesn't have explicit close method, but setting widgetOpen to false will hide backdrop
      // The actual overlay will be closed when user clicks X in MoonPay iframe
      console.log('🚪 User confirmed close')
    } catch (error) {
      console.error('Failed to close MoonPay widget:', error)
    }
  }
}

function handleBack() {
  if (widgetOpen.value) {
    handleCloseAttempt()
  } else {
    emit('back')
  }
}

onMounted(() => {
  initWidget()
})

onUnmounted(() => {
  // Cleanup
  widgetOpen.value = false
  showCloseConfirm.value = false
})
</script>

<style scoped>
/* Loading Spinner */
.spinner {
  @apply w-12 h-12 rounded-full border-4 border-vault-blue-400/10;
  border-top-color: rgba(102, 126, 234, 0.8);
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Fullscreen Backdrop for MoonPay Overlay */
.moonpay-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.75);
  z-index: 9999; /* Above everything */
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
}

/* Close Button on Backdrop */
.moonpay-close-button {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 10001; /* Above backdrop and iframe */
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.95);
  border: 2px solid rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.moonpay-close-button:hover {
  background-color: rgba(255, 255, 255, 1);
  transform: scale(1.05);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

.moonpay-close-button:active {
  transform: scale(0.95);
}

/* Confirmation Modal Backdrop */
.moonpay-confirm-modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.85);
  z-index: 10002; /* Above close button */
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(8px);
}

/* Confirmation Modal */
.moonpay-confirm-modal {
  background-color: white;
  border-radius: 16px;
  padding: 32px;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

/* Fade Transition for Backdrop */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Modal Fade Transition */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: all 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
  transform: scale(0.9);
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .moonpay-close-button {
    top: 12px;
    right: 12px;
    width: 40px;
    height: 40px;
  }

  .moonpay-confirm-modal {
    padding: 24px;
    max-width: 340px;
  }
}
</style>
